<template>
  <div class="activities-tab">
    <!-- ── Mål ── -->
    <div class="act-card">
      <h3>Mål 🎯</h3>
      <div v-if="goals.length" class="goal-list">
        <div v-for="g in goals" :key="g.id" class="goal-row">
          <div class="goal-info">
            <span class="goal-label">{{ metricLabel(g.metric) }} · {{ periodLabel(g.period) }}</span>
            <span class="goal-values">{{ fmtGoal(g.currentValue, g.metric) }} / {{ fmtGoal(g.targetValue, g.metric) }}</span>
          </div>
          <div class="goal-bar">
            <div class="goal-bar-fill" :style="{ width: Math.min(100, (g.currentValue / g.targetValue) * 100) + '%' }"
                 :class="{ done: g.currentValue >= g.targetValue }"></div>
          </div>
          <button class="btn-act-danger" title="Ta bort mål" @click="removeGoal(g)">✕</button>
        </div>
      </div>
      <p v-else class="hint">Inga mål satta än.</p>
      <div class="form-row goal-form">
        <div class="field">
          <label>Mätvärde</label>
          <select v-model="goalMetric">
            <option value="KCAL">Aktiva kalorier</option>
            <option value="GYM_SESSIONS">Gympass</option>
            <option value="ACTIVITIES">Aktiviteter</option>
            <option value="DISTANCE_KM">Distans (km)</option>
            <option value="STEPS">Steg (kommer snart)</option>
          </select>
        </div>
        <div class="field">
          <label>Period</label>
          <select v-model="goalPeriod">
            <option value="DAY">Per dag</option>
            <option value="WEEK">Per vecka</option>
            <option value="MONTH">Per månad</option>
          </select>
        </div>
        <div class="field">
          <label>Mål</label>
          <input type="number" step="1" inputmode="decimal" v-model="goalTarget" />
        </div>
        <button class="btn-act-primary goal-save" :disabled="!goalTarget || savingGoal" @click="saveGoal">
          {{ savingGoal ? 'Sparar...' : 'Sätt mål' }}
        </button>
      </div>
    </div>

    <!-- ── Logga aktivitet ── -->
    <div class="act-card">
      <h3>{{ editingId ? 'Rätta aktivitet' : 'Logga aktivitet' }} ❤️</h3>

      <div class="field">
        <label>Aktivitet</label>
        <select v-model="typeId">
          <optgroup v-for="cat in groupedTypes" :key="cat.key" :label="cat.label">
            <option v-for="t in cat.types" :key="t.id" :value="t.id">
              {{ t.isCardio ? '❤️ ' : '' }}{{ t.nameSv }}
            </option>
          </optgroup>
        </select>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Start</label>
          <input type="datetime-local" v-model="startedAt" />
        </div>
        <div class="field">
          <label>Tid</label>
          <div class="duration-row">
            <input type="number" min="0" max="24" v-model="hours" placeholder="h" />
            <span class="duration-sep">:</span>
            <input type="number" min="0" max="59" v-model="minutes" placeholder="min" />
          </div>
        </div>
      </div>

      <div v-if="selectedType?.isDistanceBased" class="form-row">
        <div class="field">
          <label>Distans (km)</label>
          <input type="number" step="0.01" inputmode="decimal" v-model="distanceKm" />
        </div>
        <div class="field">
          <label>Höjdmeter (valfritt)</label>
          <input type="number" step="1" inputmode="numeric" v-model="elevation" />
        </div>
      </div>

      <div v-else class="field">
        <label>Ansträngning: <strong>{{ rpeLabel }}</strong></label>
        <input type="range" min="0" max="10" step="1" v-model="rpe" class="rpe-slider" />
        <p class="hint">Från "ingen ansträngning" till "kan inte prata mer än ett par ord".</p>
      </div>

      <div class="field">
        <label>Anteckning (valfritt)</label>
        <input type="text" v-model="notes" maxlength="200" />
      </div>

      <div class="btn-row">
        <button class="btn-act-primary" :disabled="saving || !canSave" @click="save">
          {{ saving ? 'Sparar...' : (editingId ? 'Uppdatera' : 'Logga aktivitet') }}
        </button>
        <button v-if="editingId" class="btn-act-secondary" @click="resetForm">Avbryt</button>
      </div>
    </div>

    <!-- ── Historik ── -->
    <div class="act-card" v-if="activities.length">
      <h3>Aktiviteter</h3>
      <div class="table-wrap">
        <table class="act-table">
          <thead>
            <tr><th>Datum</th><th>Aktivitet</th><th>Tid</th><th>Distans</th><th>Tempo</th><th>kcal</th><th></th><th></th></tr>
          </thead>
          <tbody>
            <template v-for="a in activities" :key="a.id">
              <tr @click="edit(a)">
                <td>{{ formatDate(a.startedAt) }}</td>
                <td>{{ a.activityType.isCardio ? '❤️ ' : '' }}{{ a.activityType.nameSv }}</td>
                <td>{{ formatDuration(a.durationSeconds) }}</td>
                <td>{{ a.distanceMeters ? (a.distanceMeters / 1000).toFixed(2) + ' km' : '–' }}</td>
                <td>{{ pace(a) }}</td>
                <td>{{ a.estimatedKcal != null ? Math.round(a.estimatedKcal) : '–' }}</td>
                <td>
                  <button v-if="a.routePolyline" class="btn-act-map"
                          :title="expandedId === a.id ? 'Dölj rutt' : 'Visa rutt'"
                          @click.stop="toggleMap(a)">🗺</button>
                </td>
                <td><button class="btn-act-danger" @click.stop="remove(a)">✕</button></td>
              </tr>
              <tr v-if="expandedId === a.id" class="map-row">
                <td :colspan="8">
                  <div :id="'route-map-' + a.id" class="route-map"></div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <p v-else class="hint">Inga aktiviteter loggade än.</p>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Google encoded polyline (precision 1e5) → [[lat, lon], ...]
function decodePolyline(str) {
  const points = [];
  let index = 0, lat = 0, lon = 0;
  while (index < str.length) {
    for (const which of [0, 1]) {
      let result = 0, shift = 0, b;
      do {
        b = str.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const delta = (result & 1) ? ~(result >> 1) : (result >> 1);
      if (which === 0) lat += delta; else lon += delta;
    }
    points.push([lat / 1e5, lon / 1e5]);
  }
  return points;
}

const CATEGORY_LABELS = {
  WALK: 'Promenad', RUN: 'Löpning', HIKE: 'Vandring', CYCLE: 'Cykling',
  SWIM: 'Simning', WATER: 'Vatten', CARDIO: 'Kondition', FITNESS: 'Rörlighet',
  STRENGTH: 'Styrka', MARTIAL_ARTS: 'Kampsport & fäktning', SPORT: 'Sport',
  WINTER: 'Vinter', OTHER: 'Övrigt',
};

const RPE_LABELS = [
  'ingen ansträngning', 'väldigt lätt', 'lätt', 'lätt–måttlig', 'måttlig',
  'normal för aktiviteten', 'något ansträngande', 'ansträngande',
  'mycket ansträngande', 'nästan max', 'kan inte prata mer än ett par ord',
];

export default {
  name: 'ActivitiesTab',
  emits: ['toast'],
  setup(props, { emit }) {
    const types = ref([]);
    const activities = ref([]);
    const saving = ref(false);
    const editingId = ref(null);

    const typeId = ref(null);
    const startedAt = ref(defaultStart());
    const hours = ref('');
    const minutes = ref('');
    const distanceKm = ref('');
    const elevation = ref('');
    const rpe = ref(5);
    const notes = ref('');

    function defaultStart() {
      const d = new Date(Date.now() - 60 * 60 * 1000); // en timme sedan
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      return d.toISOString().slice(0, 16);
    }

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }
    function toast(msg, error = false) { emit('toast', { msg, error }); }

    const selectedType = computed(() => types.value.find(t => t.id === typeId.value));
    const rpeLabel = computed(() => RPE_LABELS[Number(rpe.value)] ?? '');
    const durationSeconds = computed(() =>
      (Number(hours.value) || 0) * 3600 + (Number(minutes.value) || 0) * 60);
    const canSave = computed(() => typeId.value && durationSeconds.value > 0);

    const groupedTypes = computed(() => {
      const order = Object.keys(CATEGORY_LABELS);
      const byCat = {};
      for (const t of types.value) (byCat[t.category] ??= []).push(t);
      return order.filter(c => byCat[c]).map(c => ({
        key: c, label: CATEGORY_LABELS[c] || c, types: byCat[c],
      }));
    });

    async function load() {
      try {
        const [td, ad] = await Promise.all([
          withToken(t => api.getActivityTypes(t)),
          withToken(t => api.getActivities({ limit: 50 }, t)),
        ]);
        types.value = td.activityTypes || [];
        activities.value = ad.activities || [];
        if (!typeId.value && types.value.length) {
          typeId.value = (types.value.find(t => t.key === 'walking_city') || types.value[0]).id;
        }
      } catch (e) { toast(`Kunde inte hämta aktiviteter: ${e.message}`, true); }
    }

    function resetForm() {
      editingId.value = null;
      startedAt.value = defaultStart();
      hours.value = ''; minutes.value = ''; distanceKm.value = '';
      elevation.value = ''; rpe.value = 5; notes.value = '';
    }

    function edit(a) {
      editingId.value = a.id;
      typeId.value = a.activityType.id;
      const d = new Date(a.startedAt);
      d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
      startedAt.value = d.toISOString().slice(0, 16);
      hours.value = Math.floor(a.durationSeconds / 3600) || '';
      minutes.value = Math.floor((a.durationSeconds % 3600) / 60) || '';
      distanceKm.value = a.distanceMeters ? (a.distanceMeters / 1000) : '';
      elevation.value = a.elevationGainMeters ?? '';
      rpe.value = a.rpe ?? 5;
      notes.value = a.notes ?? '';
    }

    async function save() {
      saving.value = true;
      try {
        const isDistance = selectedType.value?.isDistanceBased;
        const common = {
          startedAt: new Date(startedAt.value).toISOString(),
          durationSeconds: durationSeconds.value,
          distanceMeters: isDistance && distanceKm.value !== ''
            ? Number(distanceKm.value) * 1000 : (editingId.value ? -1 : null),
          elevationGainMeters: isDistance && elevation.value !== ''
            ? Number(elevation.value) : (editingId.value ? -1 : null),
          rpe: !isDistance ? Number(rpe.value) : (editingId.value ? -1 : null),
          notes: notes.value || (editingId.value ? '' : null),
        };
        if (editingId.value) {
          await withToken(t => api.updateActivity(
            { id: editingId.value, activityTypeId: typeId.value, ...common }, t));
          toast('Aktiviteten uppdaterad!');
        } else {
          await withToken(t => api.addActivity(
            { activityTypeId: typeId.value, ...common }, t));
          toast('Aktiviteten loggad!');
        }
        resetForm();
        await load();
      } catch (e) { toast(`Fel: ${e.message}`, true); }
      finally { saving.value = false; }
    }

    // ── Ruttkarta (Leaflet + OSM) ──
    const expandedId = ref(null);
    let leafletMap = null;

    async function toggleMap(a) {
      if (expandedId.value === a.id) {
        expandedId.value = null;
        if (leafletMap) { leafletMap.remove(); leafletMap = null; }
        return;
      }
      if (leafletMap) { leafletMap.remove(); leafletMap = null; }
      expandedId.value = a.id;
      await nextTick();
      const points = decodePolyline(a.routePolyline);
      if (!points.length) return;
      leafletMap = L.map('route-map-' + a.id);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap-bidragsgivare', maxZoom: 19,
      }).addTo(leafletMap);
      const line = L.polyline(points, { weight: 4 }).addTo(leafletMap);
      leafletMap.fitBounds(line.getBounds(), { padding: [20, 20] });
    }

    async function remove(a) {
      if (!confirm(`Ta bort ${a.activityType.nameSv} ${formatDate(a.startedAt)}?`)) return;
      try {
        await withToken(t => api.deleteActivity(a.id, t));
        toast('Aktiviteten borttagen');
        await load();
      } catch (e) { toast(`Fel: ${e.message}`, true); }
    }

    function formatDate(iso) {
      const d = new Date(iso);
      return `${d.toLocaleDateString('sv-SE')} ${d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`;
    }
    function formatDuration(sec) {
      const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
      return h > 0 ? `${h} h ${m} min` : `${m} min`;
    }
    function pace(a) {
      if (!a.distanceMeters || !a.durationSeconds) return '–';
      const kmh = (a.distanceMeters / 1000) / (a.durationSeconds / 3600);
      return `${kmh.toFixed(1)} km/h`;
    }

    // ── Mål ──
    const goals = ref([]);
    const goalMetric = ref('KCAL');
    const goalPeriod = ref('DAY');
    const goalTarget = ref('');
    const savingGoal = ref(false);

    const METRIC_LABELS = {
      KCAL: 'Aktiva kalorier', GYM_SESSIONS: 'Gympass', STEPS: 'Steg',
      ACTIVITIES: 'Aktiviteter', DISTANCE_KM: 'Distans',
    };
    const PERIOD_LABELS = { DAY: 'dag', WEEK: 'vecka', MONTH: 'månad' };
    function metricLabel(m) { return METRIC_LABELS[m] || m; }
    function periodLabel(p) { return PERIOD_LABELS[p] || p; }
    function fmtGoal(v, metric) {
      const n = metric === 'DISTANCE_KM' ? Number(v).toFixed(1) : Math.round(v);
      return metric === 'DISTANCE_KM' ? `${n} km` : n;
    }

    function periodStarts() {
      const now = new Date();
      const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const week = new Date(day);
      week.setDate(day.getDate() - ((day.getDay() + 6) % 7)); // måndag
      const month = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        dayStart: day.toISOString(),
        weekStart: week.toISOString(),
        monthStart: month.toISOString(),
      };
    }

    async function loadGoals() {
      try {
        const d = await withToken(t => api.getGoalsWithProgress(periodStarts(), t));
        goals.value = d.goalsWithProgress || [];
      } catch (e) { /* äldre server utan mål-stöd — visa tomt */ }
    }

    async function saveGoal() {
      savingGoal.value = true;
      try {
        await withToken(t => api.setGoal(goalMetric.value, goalPeriod.value, Number(goalTarget.value), t));
        toast('Målet sparat!');
        goalTarget.value = '';
        await loadGoals();
      } catch (e) { toast(`Fel: ${e.message}`, true); }
      finally { savingGoal.value = false; }
    }

    async function removeGoal(g) {
      try {
        await withToken(t => api.setGoal(g.metric, g.period, 0, t));
        toast('Målet borttaget');
        await loadGoals();
      } catch (e) { toast(`Fel: ${e.message}`, true); }
    }

    onMounted(() => { load(); loadGoals(); });

    return {
      types, activities, saving, editingId,
      typeId, startedAt, hours, minutes, distanceKm, elevation, rpe, notes,
      selectedType, rpeLabel, groupedTypes, canSave,
      save, edit, remove, resetForm, formatDate, formatDuration, pace,
      expandedId, toggleMap,
      goals, goalMetric, goalPeriod, goalTarget, savingGoal,
      metricLabel, periodLabel, fmtGoal, saveGoal, removeGoal,
    };
  },
};
</script>

<style scoped>
.activities-tab { display: flex; flex-direction: column; gap: 1.25rem; }

.act-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-lg); padding: 1.25rem;
  display: flex; flex-direction: column; gap: 0.8rem; box-shadow: var(--gym-shadow-sm);
}
.act-card h3 { margin: 0; font-size: 1.05rem; color: var(--gym-text); }

.field { display: flex; flex-direction: column; gap: 0.35rem; flex: 1; min-width: 140px; }
.field label { font-size: 0.85rem; font-weight: 600; color: var(--gym-text-muted); }
.field label strong { color: var(--gym-accent); }
.field input, .field select {
  padding: 0.6rem 0.8rem; border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-sm); font-size: 0.92rem;
  background: var(--gym-surface-2); color: var(--gym-text); width: 100%; box-sizing: border-box;
}
.field input:focus, .field select:focus { outline: none; border-color: var(--gym-accent); }

.form-row { display: flex; gap: 0.75rem; flex-wrap: wrap; }
.duration-row { display: flex; align-items: center; gap: 0.4rem; }
.duration-sep { color: var(--gym-text-muted); font-weight: 700; }

.rpe-slider { accent-color: var(--gym-accent); padding: 0.4rem 0 !important; border: none !important; background: none !important; }

.hint { font-size: 0.8rem; color: var(--gym-text-muted); margin: 0; }

.btn-row { display: flex; gap: 0.5rem; }
.btn-act-primary {
  background: var(--gym-accent); color: var(--gym-on-accent); border: none;
  padding: 0.6rem 1.3rem; border-radius: var(--gym-radius-sm);
  font-size: 0.92rem; font-weight: 700; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease);
}
.btn-act-primary:hover:not(:disabled) { background: var(--gym-accent-hover); box-shadow: var(--gym-shadow-accent); }
.btn-act-primary:active:not(:disabled) { transform: translateY(1px) scale(0.99); }
.btn-act-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-act-secondary {
  background: var(--gym-surface-2); color: var(--gym-text); border: 1px solid var(--gym-border);
  padding: 0.6rem 1.1rem; border-radius: var(--gym-radius-sm); font-size: 0.92rem; cursor: pointer;
}

.table-wrap { overflow-x: auto; }
.act-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; color: var(--gym-text); }
.act-table th, .act-table td {
  text-align: left; padding: 0.45rem 0.6rem;
  border-bottom: 1px solid var(--gym-border); white-space: nowrap;
}
.act-table th {
  color: var(--gym-text-dim); font-weight: 600; font-size: 0.8rem;
  text-transform: uppercase; letter-spacing: 0.03em;
}
.act-table tbody tr { cursor: pointer; transition: background var(--gym-dur) var(--gym-ease); }
.act-table tbody tr:hover { background: var(--gym-surface-2); }

.btn-act-map {
  background: none; border: none; cursor: pointer; font-size: 1rem; padding: 0.2rem 0.4rem;
}
.map-row td { padding: 0; }
.route-map {
  height: 300px; width: 100%; border-radius: var(--gym-radius-sm);
  border: 1px solid var(--gym-border); margin: 0.4rem 0;
}

.goal-list { display: flex; flex-direction: column; gap: 0.6rem; }
.goal-row { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 0.3rem 0.6rem; }
.goal-info { display: flex; justify-content: space-between; font-size: 0.9rem; color: var(--gym-text); }
.goal-label { font-weight: 600; }
.goal-values { color: var(--gym-text-muted); }
.goal-bar {
  grid-column: 1; height: 8px; border-radius: 4px;
  background: var(--gym-surface-2); overflow: hidden;
}
.goal-bar-fill { height: 100%; background: var(--gym-accent); transition: width 0.3s; }
.goal-bar-fill.done { background: #4caf50; }
.goal-row .btn-act-danger { grid-column: 2; grid-row: 1 / span 2; }
.goal-form { align-items: flex-end; }
.goal-save { white-space: nowrap; }

.btn-act-danger {
  background: none; border: none; color: var(--gym-danger, #e06666);
  cursor: pointer; font-size: 0.9rem; padding: 0.2rem 0.4rem;
}
</style>
